import Vue from 'vue'
import {
  defaultFeatureFlagValues,
  featureFlags,
  featureFlagTiming,
  FeatureFlagValues,
} from '@app/config/featureFlags'

export const featureFlagState = Vue.observable({
  values: defaultFeatureFlagValues(),
})

export interface FeatureFlagRemoteConfig {
  settings: {
    minimumFetchIntervalMillis: number
    fetchTimeoutMillis: number
  }
  readonly fetchTimeMillis: number
  defaultConfig: Record<string, string | number | boolean>
  setLogLevel(level: 'silent'): void
  ensureInitialized(): Promise<void>
  fetchAndActivate(): Promise<boolean>
  getValue(key: string): { getSource(): string; asString(): string }
}

// Initialization, Installations and storage are included in the deadline.
// A timed-out SDK call cannot later publish its values or overlap another call.
export function createFeatureFlagController(
  getRemoteConfig: () =>
    | FeatureFlagRemoteConfig
    | Promise<FeatureFlagRemoteConfig>,
  apply: (values: FeatureFlagValues) => void,
  prepareFetch: () => Promise<unknown> = () => Promise.resolve()
) {
  let remoteConfig: FeatureFlagRemoteConfig | undefined
  let inFlight: Promise<void> | undefined
  let lastSuccess: number | undefined
  let stopped = false

  function refresh(): Promise<void> {
    if (stopped) return Promise.resolve()
    if (inFlight) return inFlight
    if (
      lastSuccess !== undefined &&
      Date.now() - lastSuccess < featureFlagTiming.successCacheMillis
    )
      return Promise.resolve()
    // An expired enabled value must not remain visible during a new attempt.
    lastSuccess = undefined
    apply(defaultFeatureFlagValues())
    inFlight = runRefresh()
    return inFlight
  }

  async function runRefresh(): Promise<void> {
    let expired = false
    let workSettled = false
    let timeout: ReturnType<typeof setTimeout> | undefined
    const work = Promise.resolve()
      .then(async () => {
        if (!remoteConfig) {
          remoteConfig = await getRemoteConfig()
          remoteConfig.setLogLevel('silent')
          remoteConfig.settings = {
            minimumFetchIntervalMillis:
              featureFlagTiming.minimumFetchIntervalMillis,
            fetchTimeoutMillis: featureFlagTiming.fetchTimeoutMillis,
          }
          remoteConfig.defaultConfig = Object.fromEntries(
            Object.values(featureFlags).map((flag) => [
              flag.key,
              flag.defaultValue,
            ])
          )
        }
        await remoteConfig.ensureInitialized()
        if (expired || stopped) return defaultFeatureFlagValues()
        await prepareFetch()
        if (expired || stopped) return defaultFeatureFlagValues()
        await remoteConfig.fetchAndActivate()
        const values = defaultFeatureFlagValues()
        for (const name of Object.keys(featureFlags) as Array<
          keyof typeof featureFlags
        >) {
          const value = remoteConfig.getValue(featureFlags[name].key)
          // Missing keys and malformed values use the code default.
          if (value.getSource() === 'remote') {
            if (value.asString() === 'true') values[name] = true
            if (value.asString() === 'false') values[name] = false
          }
        }
        return values
      })
      .finally(() => {
        workSettled = true
        if (expired) inFlight = undefined
      })
    try {
      const values = await Promise.race([
        work,
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => {
            expired = true
            reject(new Error('Feature flag deadline'))
          }, featureFlagTiming.operationTimeoutMillis)
        }),
      ])
      if (!stopped) {
        lastSuccess = remoteConfig?.fetchTimeMillis
        apply(values)
      }
    } catch (_) {
      // Do not retain an activated true cache on failure. Do not log SDK errors,
      // which can include request/installation credentials and remote contents.
      if (!stopped) apply(defaultFeatureFlagValues())
    } finally {
      if (timeout) clearTimeout(timeout)
      if (workSettled) inFlight = undefined
    }
  }

  return {
    refresh,
    stop() {
      stopped = true
    },
  }
}

let requestFeatureFlags: () => Promise<void> = () => Promise.resolve()

// The client plugin registers one controller; registration itself never fetches.
export function configureFeatureFlagRequest(request: () => Promise<void>) {
  requestFeatureFlags = request
}

export function refreshFeatureFlags(): Promise<void> {
  return requestFeatureFlags()
}
